import React, { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import "../Common/eaPage.css";

const QAItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="ea-faq-item">
      <button
        type="button"
        className="ea-faq-q"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span>{question}</span>
        <FaChevronDown className={`ea-chevron${open ? " is-open" : ""}`} aria-hidden="true" />
      </button>
      {open ? <div className="ea-faq-a">{answer}</div> : null}
    </div>
  );
};

const FAQSection = ({ title, items, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="ea-card">
      <button
        type="button"
        className="ea-faq-toggle"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span>
          <span className="ea-card-title">{title}</span>
          <span className="ea-count ms-2">{items.length}</span>
        </span>
        <FaChevronDown className={`ea-chevron${open ? " is-open" : ""}`} aria-hidden="true" />
      </button>
      {open ? (
        <div className="ea-faq-list">
          {items.map((item) => (
            <QAItem key={item.question} {...item} />
          ))}
        </div>
      ) : null}
    </section>
  );
};

const FAQ_SECTIONS = [
  {
    title: "Company & Products",
    defaultOpen: true,
    items: [
      {
        question: "What is unique about EMAlpha’s Multilingual AI?",
        answer:
          "Founded in 2020, EMAlpha pioneered Multilingual AI—recognizing that only about 50% of the web is in English. Its models extract and interpret financial information in real time across dozens of languages.",
      },
      {
        question: "What are the strengths of EMAlpha’s products?",
        answer:
          "Breadth and speed. EMAlpha captures an unparalleled range of global information and delivers contextual insights faster than conventional data platforms.",
      },
    ],
  },
  {
    title: "Data & Coverage",
    items: [
      {
        question: "What are the primary data sources behind EMAlpha’s products?",
        answer:
          "EMAlpha aggregates data from thousands of local and global sources — news, filings, and government portals — covering 1,000+ distinct outlets per country.",
      },
      {
        question: "How frequently is the data refreshed?",
        answer:
          "CoTrader updates in near real time; MacroMonitor and CompanyMonitor refresh daily for country and company-level insights.",
      },
      {
        question: "How many countries and companies are covered?",
        answer:
          "MacroMonitor: 50+ countries; CompanyMonitor: 5,000+ public companies; CoTrader: FX and macro-sensitive assets worldwide.",
      },
      {
        question: "How many languages are supported?",
        answer:
          "25+ languages including Spanish, Chinese, Arabic, Russian, and Hindi — analyzed directly without translation loss.",
      },
    ],
  },
  {
    title: "Features & Insights",
    items: [
      {
        question: "How are the relevance and sentiment scores calculated?",
        answer:
          "Finance-specific AI models assess tone, materiality, and context. Relevance ranges from 0 (irrelevant) to +1 (highly relevant); Sentiment ranges from −1 (negative) to +1 (positive).",
      },
      {
        question: "How are Macro and Company themes defined?",
        answer:
          "Themes are derived from frameworks by IMF, OECD, and CFA Institute, mapping risks like inflation, governance, and systemic stability.",
      },
      {
        question: "How does CoTrader deliver real-time event detection?",
        answer:
          "CoTrader continuously tracks global policy shifts and macro events, surfacing market-moving signals instantly.",
      },
    ],
  },
  {
    title: "Customizability & Integration",
    items: [
      {
        question: "Can I customize filters?",
        answer:
          "Yes — filter by country, company, theme, time period, or language directly within the dashboard.",
      },
      {
        question: "Does CoTrader integrate with trading platforms?",
        answer:
          "Yes — integrated with State Street’s FXConnect and supports API-based or InterOperable integrations with client systems.",
      },
    ],
  },
  {
    title: "Trust & Transparency",
    items: [
      {
        question: "How does EMAlpha ensure data quality?",
        answer:
          "All data undergoes deduplication, source filtering, and relevance scoring to maintain quality and reduce noise.",
      },
      {
        question: "Is the system auditable?",
        answer: "Yes — each sentiment score links directly to its source article for full traceability.",
      },
    ],
  },
  {
    title: "Pricing & Access",
    items: [
      {
        question: "What are the pricing options?",
        answer:
          "Subscription-based (individual, team, or enterprise tiers) or custom licenses for deep integration.",
      },
      {
        question: "Is there a free trial?",
        answer: "Demo access and limited trials available on request.",
      },
    ],
  },
  {
    title: "Support & Onboarding",
    items: [
      {
        question: "What onboarding support do you provide?",
        answer:
          "Guided sessions, detailed user guides, and 'product in action' demos for new clients.",
      },
      {
        question: "Is customer support available?",
        answer: "Yes — dedicated support and analyst desks are available for enterprise clients.",
      },
    ],
  },
];

const FAQ = () => {
  const { isDark } = useTheme();

  return (
    <div className={`ea-page${isDark ? " is-dark" : ""}`}>
      <div className="ea-shell">
        <header className="ea-head">
          <p className="ea-eyebrow">Help</p>
          <h1 className="ea-title">Frequently asked questions</h1>
          <p className="ea-sub">CoTrader · MacroMonitor · CompanyMonitor</p>
        </header>

        {FAQ_SECTIONS.map((section) => (
          <FAQSection key={section.title} {...section} />
        ))}
      </div>
    </div>
  );
};

export default FAQ;
